import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { Shield, Plus, Pencil, Trash2, Save, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';

const RoleManagement = () => {
  const { apiCall } = useAuth();
  const { toast } = useToast();
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: {}
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [rolesData, permsData] = await Promise.all([
        apiCall('/api/roles/'),
        apiCall('/api/roles/permissions')
      ]);
      
      setRoles(rolesData);
      setPermissions(permsData.permissions);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load roles',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    setIsCreating(true);
    setEditingRole(null);
    const initialPerms = {};
    Object.keys(permissions).forEach(key => {
      initialPerms[key] = false;
    });
    setFormData({
      name: '',
      description: '',
      permissions: initialPerms
    });
  };

  const handleEdit = (role) => {
    setEditingRole(role.id);
    setIsCreating(false);
    const rolePerms = {};
    Object.keys(permissions).forEach(key => {
      rolePerms[key] = role.permissions[key] || false;
    });
    setFormData({
      name: role.name,
      description: role.description || '',
      permissions: rolePerms
    });
  };

  const handleCancel = () => {
    setIsCreating(false);
    setEditingRole(null);
    setFormData({ name: '', description: '', permissions: {} });
  };

  const handlePermissionChange = (permKey, checked) => {
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [permKey]: checked
      }
    }));
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast({
        title: 'Error',
        description: 'Role name is required',
        variant: 'destructive'
      });
      return;
    }

    const hasPermissions = Object.values(formData.permissions).some(val => val === true);
    if (!hasPermissions) {
      toast({
        title: 'Error',
        description: 'Please select at least one permission',
        variant: 'destructive'
      });
      return;
    }

    try {
      if (editingRole) {
        await apiCall(`/api/roles/${editingRole}`, {
          method: 'PUT',
          body: JSON.stringify(formData)
        });
        toast({
          title: 'Success',
          description: 'Role updated successfully'
        });
      } else {
        await apiCall('/api/roles/', {
          method: 'POST',
          body: JSON.stringify(formData)
        });
        toast({
          title: 'Success',
          description: 'Role created successfully'
        });
      }
      
      handleCancel();
      fetchData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save role',
        variant: 'destructive'
      });
    }
  };

  const handleDelete = async (roleId) => {
    if (!confirm('Are you sure you want to delete this role? This action cannot be undone.')) {
      return;
    }

    try {
      await apiCall(`/api/roles/${roleId}`, { method: 'DELETE' });
      toast({
        title: 'Success',
        description: 'Role deleted successfully'
      });
      fetchData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete role',
        variant: 'destructive'
      });
    }
  };

  const selectAll = () => {
    const allSelected = {};
    Object.keys(permissions).forEach(key => {
      allSelected[key] = true;
    });
    setFormData(prev => ({ ...prev, permissions: allSelected }));
  };

  const deselectAll = () => {
    const allDeselected = {};
    Object.keys(permissions).forEach(key => {
      allDeselected[key] = false;
    });
    setFormData(prev => ({ ...prev, permissions: allDeselected }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <p className="text-gray-500">Loading roles...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="h-6 w-6" />
            Role Management
          </h2>
          <p className="text-gray-600 text-sm mt-1">
            Create and manage custom roles with specific permissions
          </p>
        </div>
        {!isCreating && !editingRole && (
          <Button onClick={handleCreateNew}>
            <Plus className="h-4 w-4 mr-2" />
            Create New Role
          </Button>
        )}
      </div>

      {/* Create/Edit Form */}
      {(isCreating || editingRole) && (
        <Card>
          <CardHeader>
            <CardTitle>{editingRole ? 'Edit Role' : 'Create New Role'}</CardTitle>
            <CardDescription>Define a custom role with specific access permissions</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="role_name">Role Name *</Label>
              <Input
                id="role_name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Content Manager, Sales Agent, Support Staff"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="role_description">Description (Optional)</Label>
              <Textarea
                id="role_description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe what this role is for..."
                rows={2}
              />
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Permissions *</Label>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={selectAll}>
                    Select All
                  </Button>
                  <Button variant="outline" size="sm" onClick={deselectAll}>
                    Deselect All
                  </Button>
                </div>
              </div>
              <p className="text-sm text-gray-500">Select which sections this role can access</p>
              
              {Object.keys(permissions).length === 0 ? (
                <div className="border rounded-lg p-8 text-center">
                  <p className="text-gray-500">Loading permissions...</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 border rounded-lg p-4 max-h-96 overflow-y-auto">
                  {Object.entries(permissions).map(([key, label]) => (
                    <div key={key} className="flex items-center space-x-2">
                      <Checkbox
                        id={`perm-${key}`}
                        checked={formData.permissions[key] || false}
                        onCheckedChange={(checked) => handlePermissionChange(key, checked)}
                      />
                      <label
                        htmlFor={`perm-${key}`}
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                      >
                        {label}
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <Button onClick={handleSave}>
                <Save className="h-4 w-4 mr-2" />
                {editingRole ? 'Update Role' : 'Create Role'}
              </Button>
              <Button variant="outline" onClick={handleCancel}>
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Existing Roles List */}
      {!isCreating && !editingRole && (
        <div className="space-y-4">
          <h3 className="text-xl font-semibold">Existing Roles ({roles.length})</h3>
          
          {roles.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Shield className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No custom roles created yet</p>
                <p className="text-sm text-gray-400 mt-2">Create your first role to get started</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {roles.map((role) => (
                <Card key={role.id}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold">{role.name}</h3>
                          {role.is_default && (
                            <Badge variant="secondary">System Default</Badge>
                          )}
                        </div>
                        {role.description && (
                          <p className="text-sm text-gray-600 mb-3">{role.description}</p>
                        )}
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(role.permissions)
                            .filter(([_, hasAccess]) => hasAccess)
                            .map(([permKey]) => (
                              <Badge key={permKey} variant="outline" className="text-xs">
                                {permissions[permKey] || permKey}
                              </Badge>
                            ))}
                        </div>
                      </div>
                      {!role.is_default && (
                        <div className="flex gap-2 ml-4">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(role)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(role.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RoleManagement;
